import { postCrossSectionDxf } from "@/api/crossSectionApi";
import { selectByMigration } from "@/config/v2Migration";
import { distanceUnits } from "@/models/siteUnit.types";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";
import axios from "axios";

const resolveDistanceUnit = (unit) =>
	Object.values(distanceUnits).find((u) => u === unit) ?? distanceUnits.METERS;

const toDisplayIfNumber = (value, toDisplay) =>
	typeof value === "number" ? toDisplay(value) : value;

const actions = {
	async saveCrossSectionOfLineWorks({ rootState }, { crossSectionSetting }) {
		try {
			await axios.put(
				`${import.meta.env.VITE_API_BASE}/crossSection/${rootState.site.site_uuid}/${crossSectionSetting.id}`,
				{
					name: crossSectionSetting.name,
					leftSideWidth: crossSectionSetting.leftWidth,
					rightSideWidth: crossSectionSetting.rightWidth,
					stationInterval: crossSectionSetting.interpolationPitch,
					samplingSize: crossSectionSetting.samplingSize,
				},
			);
		} catch (err) {
			console.warn("CrossSection settings is not saved");
			//Todo inform user properly
			throw err;
		}
	},
	async exportCrossSection({ rootState }, line) {
		try {
			const siteDistanceUnit = rootState?.site?.units?.distance;
			const safeUnit = resolveDistanceUnit(siteDistanceUnit);
			const unitPort = createDistanceUnitPort(safeUnit);
			const toDisplay = (value) => toDisplayIfNumber(value, unitPort.toDisplay);

			const crossSectionData = window[
				"viewer"
			].get2DCrossSectionDataOfLineWorks(line.viewerLineId);
			const lines = [];
			for (const crossSection of crossSectionData) {
				const assetPoints = new Map();
				crossSection.left.forEach((assetLists, assetType) => {
					assetLists.forEach((assetPointLists) => {
						assetPointLists.forEach((pointList) => {
							const points = pointList.map((point) => {
								return { x: toDisplay(point[0]), y: toDisplay(point[1]) };
							});
							if (assetPoints[assetType]) {
								assetPoints[assetType].push(points);
							} else {
								assetPoints[assetType] = [points];
							}
						});
					});
				});

				crossSection.right.forEach((assetLists, assetType) => {
					assetLists.forEach((assetPointLists) => {
						assetPointLists.forEach((pointList) => {
							const points = pointList.map((point) => {
								return { x: toDisplay(point[0]), y: toDisplay(point[1]) };
							});
							if (assetPoints[assetType]) {
								assetPoints[assetType].push(points);
							} else {
								assetPoints[assetType] = [points];
							}
						});
					});
				});
				const center = {
					x: toDisplay(crossSection.referencePoint.x),
					y: toDisplay(crossSection.referencePoint.y),
					z: toDisplay(crossSection.referencePoint.z),
				};

				// Add new labels for updated cross section schema
				const label = crossSection.label || null;

				lines.push({
					assetPoints,
					center,
					type: crossSection.pointType,
					label,
				});
			}
			const siteId = rootState.site.site_uuid;
			const crossSectionId = line.crossSectionSetting.id;
			const body = {
				lines,
				leftSideWidth: toDisplay(line.crossSectionSetting?.leftWidth),
				rightSideWidth: toDisplay(line.crossSectionSetting?.rightWidth),
				stationInterval: toDisplay(
					line.crossSectionSetting?.interpolationPitch,
				),
				site_distance_unit: safeUnit,
			};
			const fetchDxf = selectByMigration("crossSection", {
				v1: async () => {
					const response = await axios.post(
						`${import.meta.env.VITE_API_BASE}/crossSection/createDXF/${siteId}/${crossSectionId}`,
						body,
					);
					return response.data;
				},
				v2: () => postCrossSectionDxf(siteId, crossSectionId, body),
			});
			const blob = new Blob([await fetchDxf()], { type: "application/dxf" });
			const link = document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download = `${line.lineName}_cross section.dxf`;
			link.click();
			URL.revokeObjectURL(link.href);
		} catch (err) {
			console.warn("Dxf download failed", err);
			throw err;
		}
	},
	async saveCrossSectionOnCreate(
		{ rootState },
		{ object, siteId, crossSectionParams },
	) {
		const res = await axios.post(
			`${import.meta.env.VITE_API_BASE}/crossSection/object/${siteId}/${object.id}`,
			{
				name: object.name,
				leftSideWidth: crossSectionParams.leftWidth,
				rightSideWidth: crossSectionParams.rightWidth,
				stationInterval: crossSectionParams.interpolationPitch,
				samplingSize: crossSectionParams.samplingSize,
			},
			{ auth: rootState.authObject },
		);
		return {
			cross_section_id: res.data.cross_section_id,
			left_side_width: res.data.left_side_width,
			right_side_width: res.data.right_side_width,
			station_interval: res.data.station_interval,
			sampling_size: res.data.sampling_size,
		};
	},

	async deleteCrossSection({ rootState }, { siteId, crossSectionId }) {
		await axios.delete(
			`${import.meta.env.VITE_API_BASE}/crossSection/${siteId}/${crossSectionId}`,
			{ auth: rootState.authObject },
		);
	},
};

export default {
	namespaced: true,
	actions,
};
